import { html, LitElement, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { SlhCoreCheckbox } from "@slhuicore/checkbox";
import { SlhCoreFormElement } from "@slhuicore/base/form";
import { SlhFormIcons } from "@slhui/form-icons";

import { style } from "./assets/checkbox.generated-style";
import * as Checkbox from "./checkbox.constants";

/**
 * Allow users to choose of multiple options from a set of options.
 *
 * @CustomElement slh-checkbox
 *
 * @cssprop --slh-default-font-family - Checkbox font family.
 * @cssprop --slh-color-neutral-shade-20 - Disabled and readonly checkbox color
 * @cssprop --slh-color-neutral-shade-40 - Checkbox color.
 * @cssprop --slh-color-neutral-shade-60 - Hover checkbox color. Checkbox hint color.
 * @cssprop --slh-color-neutral-shade-80-opacity-12 - Drop shadow color for focused checkbox.
 * @cssprop --slh-color-primary - Checked checkbox color.
 * @cssprop --slh-color-primary-darken - Checked and hovered checkbox color.
 * @cssprop --slh-color-error-shade-40 - Drop shadow color for focused error state checkbox.
 * @cssprop --slh-color-error-shade-60 - Error unselected color.
 * @cssprop --slh-color-error - Error checked background color, error unselected hover color.
 * @cssprop --slh-color-error-darken - Error checked hover background color.
 * @cssprop --slh-color-neutral-shade-80-opacity-25 - Drop shadow color for focused checkbox.
 * @cssprop --slh-color-primary-darken-opacity-25 - Drop shadow color for focused checked checkbox.
 * @cssprop --slh-color-error-opacity-25 - Drop shadow color for focused error state checkbox.
 * @cssprop --slh--base-bold-font-weight - Checkbox hint font weight.
 * @cssprop --slh--base-xxs-line-height - Checkbox hint line height.
 * @cssprop --slh--base-xxs-font-size - Checkbox hint font size.
 *
 * ** slots
 * @slot default - Label for checkbox. Assigned slot nodes are parsed so that only slh-label components are shown.
 * @slot hint - Custom content below the checkbox, designed to contain recommendations for user input.
 */
@customElement("slh-checkbox")
export default class SlhCheckbox
  extends SlhCoreCheckbox(LitElement)
  implements SlhCoreFormElement
{
  static styles = style;

  // eslint-disable-next-line class-methods-use-this
  private getSelectedIconName(): string {
    return Checkbox.SELECTED_ICO;
  }

  // eslint-disable-next-line class-methods-use-this
  private getIndeterminateIconName(): string {
    return Checkbox.INDETERMINATE_ICO;
  }

  // eslint-disable-next-line class-methods-use-this
  private getDeselectedIconName(): string {
    return Checkbox.DESELECTED_ICO;
  }

  /**
   * @returns {TemplateResult} render html template
   */
  renderIcon(): TemplateResult {
    return html`<slh-icon type="${this.getIconName()}"></slh-icon>`;
  }

  // eslint-disable-next-line class-methods-use-this
  private getHintIcon(): HTMLElement {
    return SlhFormIcons.getHintIcon();
  }
}
